import React from "react";
import { useBCMSContext, BCMSStateManagerCRUD, BCMSRecord, useRoute, useWorkflow, BCMSDefaultModel } from "bcms-react";
import Classes, { ACHTemplate, ACHToParticipant, Company, CompanyAccount, NonNachaParticipants, Participant } from "model";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView } from "components/shared";
import NonNachaFileImportForm from "components/pages/NonNachaFileImportPage/components/NonNachaFileImportForm";
import NonNachaTemplateListWidget from "components/pages/NonNachaFileImportPage/components/NonNachaTemplateListWidget";

/**
 * @function NonNachaFileImportPage
 * @return {React.Component}
 * @exported true
 * @description The main component for the NonNachaFileImportPage.
 */
export default function NonNachaFileImportPage(props) {
    const navigate = useRoute();
    const { nextStep } = useWorkflow() ?? {};
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);

    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .submit(setState);
        },
        onCompanySelect: prop => value => {
            const isCompanySelected = value instanceof CompanyAccount ? true : false;
         
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .update(["ui", "isCompanySelected"], isCompanySelected)
                .submit(setState);
        },
        onChooseFile: () => {
            BCMSStateManagerCRUD.init(state)
                .update(["ui", "isFileChosen"], true)
                .submit(setState);
        },
        onContinue: () => {

            let nextParticipantId = Participant.nextID();
            const nonNachaParticipants = NonNachaParticipants.initAll().reduce((acc, item) => {

                const added = Participant.init({
                    ...item.record,
                    id: nextParticipantId,
                    isSelected: "true"
                });

                BCMSRecord.batch([
                    Participant.create(added)
                ], context, Classes);

                nextParticipantId = parseFloat(nextParticipantId) + 1
                acc.push(added);

                return acc;
            }, []);

            //non NACHA ACH To Participants 
            let nextACHToParticipantId = ACHToParticipant.nextID();
            const achToParticipant = nonNachaParticipants.reduce((acc, item) => {
                const added = ACHToParticipant.init({
                    id: nextACHToParticipantId,
                    participant: Participant.init({ ...item.record, isSelected: "true" }),
                    notify: "No",
                    addenda: "0",
                    numberOfDistributions: "1",
                    amount: 1
                });

                nextACHToParticipantId = parseFloat(nextACHToParticipantId) + 1
                acc.push(added);

                return acc;
            }, []);

            //Participants
            const participants = Participant.initAll().filter(item=> item.isSelected !=="true",[]);
            
            // Merge records
            const defaultTemplate = data.defaultTemplate;
            const mergedTemplate = BCMSDefaultModel.mergeRecords(data.template.record, defaultTemplate)
            const updateTemplate = ACHTemplate.init({ ...mergedTemplate, name: "" });
        
            BCMSStateManagerCRUD.init(state)
                .update(["data", "participants"], participants)
                .update(["data", "template"], updateTemplate)
                .update(["data", "achToParticipants", "participants"], achToParticipant)
                .update(["ui", "isFileChosen"], false)

                .submit(setState);

            nextStep({ data, ui });
            window.scrollTo(0, 0);
        }
    });
    return (
        <PageContainer className="non-nacha-file-import-page" state={{ data, ui }} controller={controller}>
            <NonNachaFileImportPageContent />
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    selectedCompany: "chooseCompany",
    companys: CompanyAccount.initAll(),
    selectedTemplate: "New Template",
    templates: [{ id: "0", value: "New Template" }],
    selectedtransactionType: "chooseTransactionType",
    transactionsType: [
        { id: "0", value: "CCD Credit - Non-Consumer Credit" },
        { id: "1", value: "CCD Debit - Non-Consumer Debit" },
        { id: "2", value: "PPD Credit - Consumer Credit" },
        { id: "3", value: "PPD Debit - Consumer Debit" },
        { id: "4", value: "Payroll - PPD Payments Prompt on Import true" }
    ],
    achToParticipants: {
        participants: []
    },
    participants: [],
    template: ACHTemplate.init({
        id: ACHTemplate.nextID(),
        transactionType: "payment",
        secCode: "PPD Credit - Consumer Credit",
        name: "",
        companyDiscretionaryData: "",
        companyEntryDescription: "",
        creditAmount: "0.00",
        debitAmount: "0.00",
        achToParticipants: [],
        achFromAccount: [],
        company: Company.initByID(0),
        companyAccount: CompanyAccount.initByID(0),
        numberOfAccounts: "0"
    }),
    defaultTemplate:
        ACHTemplate.init({
            id: ACHTemplate.nextID(),
            transactionType: "payment",
            secCode: "PPD Credit - Consumer Credit",
            name: "SamplePPD",
            companyDiscretionaryData: "",
            companyEntryDescription: "",
            creditAmount: "0.00",
            debitAmount: "0.00",
            achToParticipants: [],
            achFromAccount: [],
            company: Company.initByID(0),
            companyAccount: CompanyAccount.initByID(0),
            numberOfAccounts: "0"
        }),
    numberOfHeaderRows: "",
    numberOfFooterRows: "",
    inputDecimalsIntoAmounts: "No",
    textQualifier: "None",
    fileFormat: "Comma",
    chosenFileName: "SamplePPD.csv",
});

export const getDefaultUI = () => ({
     isFileChosen: false,
     isCompanySelected: false,
     companySelect: { isActive: false },
     transactionTypeSelect: { isActive: false }
});

/**
 * @function NonNachaFileImportPageContent
 * @return {React.Component}
 * @exported true
 * @description The page content for the NonNachaFileImportPage.
 */
function NonNachaFileImportPageContent(props) {
    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Non-NACHA File Import" />
                <NonNachaFileImportForm />
                <NonNachaTemplateListWidget />
            </CenterContentContainer>
        </PageContentView>
    )
}
